/**
 * Conservative same-human grouping for the Daily Cockpit.
 *
 * Each appointment row is a patient×coverage doc: the scrape delivers one
 * record per insurance the patient carries, all sharing that appointment's
 * patient, provider and time, so one human with medical + vision coverage
 * renders as two rows. Rows differ by coverage and nothing else — which is
 * why the row count IS the coverage count. Grouping collapses those rows
 * into one patient entry for display and counting, so the header can state
 * both truths: "38 patients · 45 coverages".
 *
 * The key is deliberately conservative: clinic + day + normalized name + DOB,
 * ALL present, or the row stands alone. The failure directions aren't
 * symmetric — a false split (one human counted twice) is the pre-grouping
 * status quo, while a false merge (two humans collapsed into one) hides a
 * person — so any missing identity field means "don't group". Same
 * philosophy as resolveMember/cardAttribution: a partial match never merges.
 */

/** The identity subset the grouping reads — structurally satisfied by
 *  AppointmentPatient and its cockpit enrichments. */
export interface PatientIdentityRow {
  id: string
  firstName: string
  lastName: string
  dob: string | null
  /** MM/DD/YYYY — constant within a day bucket, part of the key so
   *  cross-day lists (if ever grouped) don't merge separate visits. */
  appointmentDate?: string
  clinicId?: string
}

export interface PatientGroup<T extends PatientIdentityRow> {
  key: string
  rows: T[]
}

/** Trim, lowercase, collapse inner whitespace — the two sources spell the
 *  same name with drift in case/spacing, never (reliably) in letters. */
function normName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Same-human key for a row, or null when any identity field is missing —
 * a row we can't fully identify must never merge with another.
 */
export function patientGroupKey(p: PatientIdentityRow): string | null {
  const first = normName(p.firstName ?? '')
  const last = normName(p.lastName ?? '')
  const dob = (p.dob ?? '').trim()
  if (!first || !last || !dob) return null
  return [p.clinicId ?? '', p.appointmentDate ?? '', last, first, dob].join('|')
}

/** Key that never merges incomplete identities: falls back to the row's own
 *  doc id (unique per clinic — the identity hash includes clinicId). */
function keyOrSelf(p: PatientIdentityRow): string {
  return patientGroupKey(p) ?? `#${p.clinicId ?? ''}|${p.id}`
}

/**
 * Group a sorted list by human, preserving first-appearance order: a group
 * sits where the current sort placed its first row, and later rows of the
 * same human are pulled up next to it (a grouped table can't preserve a pure
 * per-row sort). Rows keep their sort order within the group.
 */
export function groupPatients<T extends PatientIdentityRow>(rows: T[]): PatientGroup<T>[] {
  const byKey = new Map<string, PatientGroup<T>>()
  const out: PatientGroup<T>[] = []
  for (const row of rows) {
    const key = keyOrSelf(row)
    let group = byKey.get(key)
    if (!group) {
      group = { key, rows: [] }
      byKey.set(key, group)
      out.push(group)
    }
    group.rows.push(row)
  }
  return out
}

/** Distinct-human count (order-independent) — the "patients" number the
 *  cockpit header and day tiles show; row count is the coverages number. */
export function countPatients(rows: PatientIdentityRow[]): number {
  const keys = new Set<string>()
  for (const row of rows) keys.add(keyOrSelf(row))
  return keys.size
}
